<template>
    <div class="card">
        <div v-if="props.showLabel">
            <slot name="label">
                <label for="category">File Uploads:</label>
            </slot>
        </div>
        <FileUpload
            ref="uploadRef"
            @uploader="fileUploader"
            customUpload
            :multiple="true"
            :fileLimit="5"
            :maxFileSize="4194304"
            invalidFileSizeMessage="{0} exceeds the {1} file size limit."
            :accept="acceptedFormats"
            :showUploadButton="false"
            :showCancelButton="false"
            @select="fileUpdates"
            @remove="fileUpdates"
        >
            <template #content="{ files, removeFileCallback }">
                <div v-if="files.length > 0">
                    <h5>Selected Files</h5>
                    <div class="flex flex-wrap p-0 sm:p-5 gap-5">
                        <div v-for="file of files" :key="file.name + file.type + file.size" class="card m-0 px-6 flex flex-column border-1 surface-border align-items-center gap-3">
                            <div>
                                <!-- {{ file }} -->
                                <img role="presentation" :alt="file.name" :src="file.objectURL" width="100" height="50" class="shadow-2" />
                            </div>
                            <span class="font-semibold">{{ file.name }}</span>
                            <div>{{ formatSize(file.size) }}</div>
                            <!-- <Badge value="Ready to upload" severity="success" /> -->
                            <Button icon="pi pi-times" @click="onRemoveTemplatingFile(removeFileCallback, index)" outlined rounded severity="danger" />
                        </div>
                    </div>
                </div>
            </template>
            <template #empty>
                <div class="flex align-items-center justify-content-center flex-column">
                    <i class="pi pi-cloud-upload border-2 border-circle p-5 text-8xl text-400 border-400" />
                    <p class="mt-4 mb-0">Drag and drop files to here to upload.</p>
                </div>
            </template>
        </FileUpload>
    </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { ref, defineExpose } from 'vue';
import { fileStore } from '../../store/global-stores';

const emit = defineEmits(['has-files', 'files-uploaded']);
const props = defineProps({
    index: {
        type: [Number, String],
    },
    showLabel: {
        type: Boolean,
        default: true,
    },
});

// ======> Store states
const { files } = storeToRefs(fileStore);

// ======> This function can be triggered by the parent component which is using CustomFileUpload component
defineExpose({
    async uploadFiles() {
        if (uploadRef.value.files.length > 0) {
            fileUploader();
        } else {
            return;
        }
    },
});

// ====> States
// =======> Props index is used as a way to differentiate different references incase same component is used multiple times at once
const uploadRef = ref(props.index + 'attachments');
const acceptedFormats =
    'image/*,.pptx, .xlsx, .xltx, .docx, .ppt, .xls, .xlt, .doc, .eml, .jpeg, .jpg, .jpe, .png, .gif, .tif, .tiff, .csv, .rtf, .pdf, .odc, .otc, .odb, .odf, .odft, .odg, .otg, .odi, .oti, .odp, .otp, .ods, .ots, .odt, .odm, .ott, .oth, .dwg, .msg';

// ========> Triggers file uploading on the server
const fileUploader = async () => {
    files.value = uploadRef.value.files;
    const filesUploadedData = await fileStore.uploadFiles();
    if (filesUploadedData) {
        console.log('EMIT');
        emit('files-uploaded', { refIndex: props.index, files: filesUploadedData });

        // remove the uploaded file graphics.
        uploadRef.value.files = [];
        files.value = [];
    }
};

// =========> Keeps track of count of files available on the component and emits existence of file in Boolean value
const fileUpdates = (event) => {
    files.value = event.files;

    emit('has-files', { refIndex: props.index, hasFile: event.files.length > 0 });
};

// ==========> Triggers if any file is removed from the dropzone
const onRemoveTemplatingFile = (removeFileCallback, index) => {
    removeFileCallback(index);
};

const formatSize = (bytes) => {
    const k = 1024;
    const dm = 3;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    if (bytes === 0) {
        return `0 ${sizes[0]}`;
    }

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

    return `${formattedSize} ${sizes[i]}`;
};
</script>
